import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { httpInterceptorProviders } from '@client-monorepo/common/network';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HammerModule } from '@angular/platform-browser';
import 'hammerjs';
import { provideServiceWorker } from '@angular/service-worker';
import { JalaliDatePipe } from '@digipay/ng-lib-pipes';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NgxSmartDialogModule, SmartDialogConfig } from '@digipay/ngx-smart-dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { EnvironmentConfig, NgxApiConfigModule } from '@digipay/ngx-api-config';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { httpWealthInterceptorProviders } from '@client-monorepo/applets/wealth';
import { createErrorHandler, TraceService } from '@sentry/angular-ivy';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { CREDIT_ENVIRONMENT, getCreditEnvironment } from '@client-monorepo/applets/credit';

const smartDialogConfig: SmartDialogConfig = {
  defaultWidth: 'auto',
  defaultHeight: 'auto',
  defaultMaxWidth: '500px',
  defaultMaxHeight: '600px',
  dialogBottomSheetBreakPoint: '1400px',
};
const environmentConfig: EnvironmentConfig = {
  agents: {
    web_agent: environment.agents.web_agent,
    android_agent: environment.agents.android_agent,
    ios_agent: environment.agents.ios_agent,
  },
  client_id: {
    web_clientId: environment.client_ids.web_username,
    android_clientId: environment.client_ids.android_hybrid_username,
    ios_clientId: environment.client_ids.ios_hybrid_username,
  },
  client_secret: {
    web_client_secret: environment.client_secrets.web_password,
    android_client_secret: environment.client_secrets.android_hybrid_password,
    ios_client_secret: environment.client_secrets.ios_hybrid_password,
  },
  digipayVersion: environment.digipay_version,
};
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes),
    provideHttpClient(withInterceptorsFromDi()),
    httpInterceptorProviders,
    httpWealthInterceptorProviders,
    { provide: 'APP_ENV', useValue: environment },
    importProvidersFrom(HammerModule),
    importProvidersFrom(MatDialogModule),
    importProvidersFrom(NgxSmartDialogModule.withConfig(smartDialogConfig)),
    importProvidersFrom(NgxApiConfigModule.environmentConfig(environmentConfig)),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: 'googleTagManagerId', useValue: environment.google_tag_manager_id },
    JalaliDatePipe,
    MatBottomSheet,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    importProvidersFrom(WealthNavigationService),
    {
      provide: ErrorHandler,
      useFactory: customErrorHandlerFactory,
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    {
      provide: 'STATE_BOTTOM_SHEET',
      useClass: NgxBottomSheetService,
    },
    {
      provide: CREDIT_ENVIRONMENT,
      useFactory: getCreditEnvironment,
    },
  ],
};
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'wealth-assets/i18n/', '.json');
}

export function customErrorHandlerFactory() {
  // Return a new error handler function that adds our refresh logic
  return {
    handleError: (error: any) => {
      console.log('error: ', error);
      // Check for the specific dynamic import error
      if (
        error instanceof TypeError &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
          error.message.includes('Importing a module script failed'))
      ) {
        console.log('Dynamic import error detected, refreshing page...');
        window.location.reload();
      }
    },
  };
}
